import { useRef, useState } from 'react'
import type { Denial, RosterImport as Resultado } from './api'
import { denialText, translate, type Lang } from './i18n'

/**
 * Subir la plantilla o la flota exportada del sistema de la agencia (§8.1).
 *
 * La pantalla está armada alrededor de lo que hay que decidir después de subir, no alrededor de
 * lo que devuelve la API. Quien sube un archivo de ochocientas líneas necesita contestar tres
 * cosas: cuántos entraron, qué filas hay que corregir, y —la que importa— **quiénes ya estaban y
 * no venían**, porque eso normalmente significa que el export salió incompleto.
 */
export function RosterImportPanel({
  lang, kind, onImport, onDone,
}: {
  lang: Lang
  kind: 'officers' | 'vehicles'
  onImport: (file: File) => Promise<Resultado>
  /** Para recargar el catálogo: lo que se acaba de subir ya cambió lo que se está mirando. */
  onDone: () => void
}) {
  const [archivo, setArchivo] = useState<File | null>(null)
  const [subiendo, setSubiendo] = useState(false)
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const [problema, setProblema] = useState<Denial | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const subir = async () => {
    if (!archivo || subiendo) return
    setSubiendo(true)
    setResultado(null)
    setProblema(null)
    try {
      setResultado(await onImport(archivo))
      // El archivo se limpia al terminar: dejarlo cargado invita a subirlo dos veces, y aunque
      // reimportar sea idempotente, la segunda corrida ensucia la bitácora sin aportar nada.
      setArchivo(null)
      if (input.current) input.current.value = ''
      onDone()
    } catch (e) {
      setProblema(e instanceof Error && 'denial' in e ? (e as { denial: Denial }).denial : null)
    } finally {
      setSubiendo(false)
    }
  }

  return (
    <section className="panel">
      <h2>{translate(lang, `import.${kind}`)}</h2>
      <p className="notice">{translate(lang, 'import.help')}</p>

      {/* Sin `grow` en el input y con permiso para partirse en dos renglones. Un input de archivo
          tiene un ancho propio grande, y estirándolo empujaba el botón fuera de la pantalla en el
          panel lateral: quedaba una pantalla donde se puede elegir el archivo y no subirlo. */}
      <div className="row-line" style={{ flexWrap: 'wrap' }}>
        <input
          ref={input}
          type="file"
          accept=".csv,.xlsx"
          style={{ maxWidth: '100%' }}
          disabled={subiendo}
          onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setResultado(null); setProblema(null) }}
          aria-label={translate(lang, 'import.file')}
        />
        <button onClick={() => void subir()} disabled={!archivo || subiendo}>
          {translate(lang, subiendo ? 'import.uploading' : 'import.upload')}
        </button>
      </div>

      {problema && <p className="notice error">{denialText(lang, problema)}</p>}

      {resultado && <Resumen lang={lang} resultado={resultado} />}
    </section>
  )
}

/** Cuántos ausentes se alcanzan a revisar uno por uno. */
const MAX_AUSENTES = 20

/**
 * Si el archivo claramente no traía la plantilla entera.
 *
 * Solo cambia el tono del aviso, nunca lo que hace el producto: no se da de baja a nadie en
 * ninguno de los dos casos. Por eso puede salir de una regla simple sin que equivocarse cueste
 * nada —a lo sumo, un aviso en amarillo donde bastaba uno gris—.
 */
function parcial(r: Resultado): boolean {
  return r.known > 0 && r.known - r.absent.length < r.known / 2
}

function Resumen({ lang, resultado }: { lang: Lang; resultado: Resultado }) {
  return (
    <div className="card">
      <p>
        <strong>{translate(lang, 'import.written', [resultado.written, resultado.read])}</strong>
      </p>

      {/* Lo primero, aunque la corrida haya salido bien: el producto no da de baja a nadie, pero
          tampoco lo esconde.

          El aviso lleva **la proporción** y no el número suelto, y eso es lo que lo hace legible.
          «576 no venían» asusta igual con un archivo de dos filas que con uno de quinientas setenta
          y ocho; «2 de 578 vinieron» se lee como lo que es —una carga parcial— y «573 de 578» se
          lee como lo que también es: que cinco personas desaparecieron del sistema de personal.

          Sale del dato y no de una declaración de quien sube a propósito. Una casilla de «este
          archivo es parcial» sería justo la que se marca para callar la advertencia. */}
      {resultado.absent.length > 0 && (
        <>
          <p className={parcial(resultado) ? 'notice' : 'notice warning'}>
            {translate(lang, 'import.absent',
              [resultado.known - resultado.absent.length, resultado.known, resultado.absent.length])}
          </p>
          {/* Los nombres solo si se pueden leer. La lista existe para ir a revisar quién falta, y
              a quinientos nadie los revisa: ahí el número es la información y los nombres, ruido. */}
          {resultado.absent.length <= MAX_AUSENTES && (
            <p className="meta">{resultado.absent.join(', ')}</p>
          )}
        </>
      )}

      {resultado.rejected > 0 ? (
        <>
          <p className="notice">{translate(lang, 'import.rejected', [resultado.rejected])}</p>
          {/* Línea, columna y motivo: es lo que hace falta para ir a corregir el archivo. Un
              «tres filas con problemas» no sirve en uno de ochocientas líneas. */}
          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'import.line')}</th>
                <th>{translate(lang, 'import.column')}</th>
                <th>{translate(lang, 'import.problem')}</th>
              </tr>
            </thead>
            <tbody>
              {resultado.problems.slice(0, 50).map((p, i) => (
                <tr key={`${p.line}-${p.column}-${i}`}>
                  <td>{p.line > 0 ? p.line : '—'}</td>
                  <td>{p.column || '—'}</td>
                  {/* El estatus ilegible dice cuáles sí se entienden: sin eso, quien corrige el
                      archivo adivina (PD-07b). */}
                  <td>{translate(lang, p.code === 'bad_value' && p.column === 'status'
                    ? 'import.problem.bad_status' : `import.problem.${p.code}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {resultado.problems.length > 50 && (
            <p className="meta">{translate(lang, 'import.more', [resultado.problems.length - 50])}</p>
          )}
          <p className="notice">{translate(lang, 'import.fixAndRetry')}</p>
        </>
      ) : (
        <p className="notice">{translate(lang, 'import.clean')}</p>
      )}

      {/* Lo que no se usó, dicho: las personales se descartan a propósito y se nombran para que
          quien sube sepa que no quedaron guardadas; las desconocidas, porque una que sí hacía
          falta con un encabezado que no conocemos se perdería en silencio (H-82). */}
      {(resultado.ignoredPersonal?.length ?? 0) > 0 && (
        <p className="meta">{translate(lang, 'import.ignoredPersonal', [resultado.ignoredPersonal!.join(', ')])}</p>
      )}
      {/* Lo que el archivo no traía y se conservó: sin decirlo, quien sube no sabría si las
          certificaciones siguen ahí o se borraron (H-84). */}
      {(resultado.kept?.length ?? 0) > 0 && (
        <p className="meta">{translate(lang, 'import.kept',
          [resultado.kept!.map((k) => translate(lang, `import.field.${k}`)).join(', ')])}</p>
      )}
      {(resultado.ignoredUnknown?.length ?? 0) > 0 && (
        <p className="notice warning">{translate(lang, 'import.ignoredUnknown', [resultado.ignoredUnknown!.join(', ')])}</p>
      )}
    </div>
  )
}
